import { LOCALIZED_PATHS } from '../../../../../utils/constants/url-constants';
import { buildSrpFacetHref } from './buildSrpFacetHref';
import { getFirstConcreteLocationSegment } from './job-detail-location-parts';
import { buildBreadcrumbListJsonLd } from '../../../../../utils/seo/build-breadcrumb-list-json-ld';
import {
  resolveStructuredJobLocations,
  type StructuredJobLocationDisplay,
  type StructuredLocationSource,
} from '../../../../../utils/location/structured-job-location-display';

type SupportedLocale = 'en' | 'fi' | 'sv';

function toSupportedLocale(locale: string): SupportedLocale {
  const n = locale.toLowerCase();
  if (n === 'fi' || n === 'sv') return n;
  return 'en';
}

/** Short label for the location breadcrumb (facet value is Algolia-facing, e.g. "Helsinki, Finland"). */
export function formatJobBreadcrumbLocationLabel(locationFacet: string): string {
  const t = locationFacet.trim();
  if (!t) return '';
  const stripped = t.replace(/,\s*(Finland|Suomi)$/i, '').trim();
  return stripped || t;
}

/**
 * Structured (TEC-482) location for a job record, or null when the job carries none (not yet
 * backfilled, or a foreign city the gazetteer doesn't seed), in which case the breadcrumb falls
 * back to the legacy `location` strings. Shared by the visible breadcrumb and the JSON-LD so the
 * two can't disagree.
 */
export function resolveJobBreadcrumbStructuredLocation(
  job: StructuredLocationSource | null | undefined,
  locale: string,
): StructuredJobLocationDisplay | null {
  return resolveStructuredJobLocations(job, { locale: toSupportedLocale(locale), hideForeignWhenFinlandPresent: false });
}

export function resolveJobPageBreadcrumbModel(input: {
  locale: string;
  location: string | string[] | undefined | null;
  /** When set (new location system, job has structured data), replaces the legacy `location` strings. */
  structuredLocation?: StructuredJobLocationDisplay | null;
}): {
  jobsHref: string;
  locationHref: string | null;
  locationLabel: string | null;
} {
  const loc = toSupportedLocale(input.locale);
  const jobsHref = `/${loc}/${LOCALIZED_PATHS[loc].jobs}`;
  if (input.structuredLocation) {
    // A global remote scope has no placeId to link to; a crumb that goes nowhere is worse than none.
    const { placeId, label } = input.structuredLocation.primary;
    if (!placeId || !label.trim()) return { jobsHref, locationHref: null, locationLabel: null };
    return { jobsHref, locationHref: buildSrpFacetHref(loc, 'regionTags', placeId), locationLabel: label };
  }
  const primary = getFirstConcreteLocationSegment(input.location);
  if (!primary.trim()) {
    return { jobsHref, locationHref: null, locationLabel: null };
  }
  return {
    jobsHref,
    locationHref: buildSrpFacetHref(loc, 'location', primary),
    locationLabel: formatJobBreadcrumbLocationLabel(primary),
  };
}

/** Matches the Home crumb convention already used on company profile pages
 * (build-company-profile-json-ld.ts) — kept in sync manually since the two builders don't share
 * a locale-copy module yet. */
function homeCrumb(locale: SupportedLocale, baseUrl: string): { name: string; url: string } {
  const path = locale === 'en' ? '/' : locale === 'fi' ? '/fi' : '/sv';
  const name = locale === 'fi' ? 'Etusivu' : locale === 'sv' ? 'Hem' : 'Home';
  return { name, url: `${baseUrl}${path}` };
}

export function buildJobDetailBreadcrumbListSchema(input: {
  baseUrl: string;
  locale: string;
  breadcrumbJobsName: string;
  jobTitle: string;
  jobCanonicalUrl: string;
  location: string | string[] | undefined | null;
  structuredLocation?: StructuredJobLocationDisplay | null;
}): Record<string, unknown> {
  const loc = toSupportedLocale(input.locale);
  const { jobsHref, locationHref, locationLabel } = resolveJobPageBreadcrumbModel({
    locale: input.locale,
    location: input.location,
    structuredLocation: input.structuredLocation,
  });
  // TEC-472 Section 9: previously started at "Työpaikat"/"Jobs" with no Home crumb, unlike the
  // company page's Home > Companies > Company trail — now consistent across both templates.
  // buildBreadcrumbListJsonLd drops any crumb with an empty name (e.g. an untranslated
  // location) instead of emitting GSC's "unnamed item" — the location crumb below no longer
  // needs its own presence guard for that reason, though it's kept since locationHref/label are
  // still a pair (an href with no label, or vice versa, would be a different bug worth catching).
  return buildBreadcrumbListJsonLd([
    homeCrumb(loc, input.baseUrl),
    { name: input.breadcrumbJobsName, url: `${input.baseUrl}${jobsHref}` },
    ...(locationHref && locationLabel ? [{ name: locationLabel, url: `${input.baseUrl}${locationHref}` }] : []),
    { name: input.jobTitle, url: input.jobCanonicalUrl },
  ]);
}
